// Declare grecaptcha for TypeScript
declare global {
    interface Window {
        grecaptcha: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

export class ReportForm {
    private isSubmitting: boolean = false;
    private form: HTMLFormElement | null = null;
    private submitButton: HTMLButtonElement | null = null;
    private recaptchaInput: HTMLInputElement | null = null;
    private recaptchaSiteKey: string;

    constructor(container: HTMLElement) {
        this.recaptchaSiteKey = container.dataset.recaptchaSiteKey || '';
        this.init(container);
    }

    private init(container: HTMLElement): void {
        this.form = container.querySelector<HTMLFormElement>('[data-js-inner="form"]');
        if (!this.form) {
            console.warn('Report form not found');
            return;
        }

        this.submitButton = container.querySelector<HTMLButtonElement>('[data-js-inner="submit-button"]');
        this.recaptchaInput = container.querySelector<HTMLInputElement>('[data-js-inner="recaptcha-input"]');
        
        if (this.form) {
            this.form.addEventListener('submit', (e) => this.handleSubmit(e));
        }
    }

    private handleSubmit(e: Event): void {
        e.preventDefault();

        if (this.isSubmitting) {
            return;
        }

        // Set submitting state
        this.isSubmitting = true;
        this.setSubmittingState(true);

        // Wait for reCAPTCHA to be available
        this.waitForRecaptcha()
            .then((grecaptcha) => {
                grecaptcha.ready(() => {
                    grecaptcha.execute(this.recaptchaSiteKey, { action: 'submit' })
                        .then((token: string) => {
                            if (this.recaptchaInput) {
                                this.recaptchaInput.value = token;
                            }
                            if (this.form) {
                                this.form.submit();
                            }
                        })
                        .catch((error: Error) => {
                            // Reset submitting state on error
                            this.resetSubmittingState();
                            console.error('reCAPTCHA error:', error);
                        });
                });
            })
            .catch((error: Error) => {
                // Reset submitting state on error
                this.resetSubmittingState();
                console.error('reCAPTCHA not loaded:', error);
            });
    }

    private waitForRecaptcha(maxWait: number = 5000): Promise<Window['grecaptcha']> {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            
            const checkRecaptcha = () => {
                const grecaptcha = window.grecaptcha;
                if (grecaptcha) {
                    resolve(grecaptcha);
                    return;
                }
                
                if (Date.now() - startTime > maxWait) {
                    reject(new Error('reCAPTCHA script failed to load within timeout'));
                    return;
                }
                
                setTimeout(checkRecaptcha, 100);
            };
            
            checkRecaptcha();
        });
    }

    private setSubmittingState(submitting: boolean): void {
        if (this.submitButton) {
            this.submitButton.disabled = submitting;
            if (submitting) {
                this.submitButton.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Submitting...';
            }
        }
    }

    private resetSubmittingState(): void {
        this.isSubmitting = false;
        if (this.submitButton) {
            this.submitButton.disabled = false;
            this.submitButton.innerHTML = 'Submit Report';
        }
    }
}

